import { FOIL_V2_GLSL } from './foilV2';

// Experimental procedural foil only. No source image is sampled or altered.
// A single shared WebGL context renders into visible cards' 2D overlay canvases.
export const FOIL_STYLES = [
  ['normal', 'Normal / non-holo', 0, 0, 'No overlay: reference image.'],
  ['classic', 'Classic holo', 1, 1, 'Rainbow sheen in the illustration window.'],
  ['cosmos', 'Cosmos / galaxy holo', 2, 1, 'Fine galaxy discs with crisp edges and bright star centres.'],
  ['vertical', 'Modern vertical holo', 3, 1, 'Narrow vertical foil columns.'],
  ['horizontal', 'Horizontal-line holo', 4, 1, 'Horizontal bands of reflected light.'],
  ['reverse', 'Reverse holo', 1, 2, 'Foil outside the illustration window.'],
  ['reverse-cosmos', 'Cosmos reverse', 2, 2, 'Galaxy pattern around the illustration.'],
  ['crosshatch', 'Crosshatch reverse', 5, 2, 'Crossed diagonal micro-lines.'],
  ['pokeball', 'Poké Ball reverse', 6, 2, 'Repeated ball motifs, not an actual scan.'],
  ['masterball', 'Master Ball reverse', 7, 2, 'Ball motifs with an approximate M accent.'],
  ['snowflake', 'Holiday snowflake foil', 8, 2, 'Six-armed winter sparkle pattern.'],
  ['cracked-ice', 'Cracked ice', 9, 3, 'Faceted, broken-ice reflections.'],
  ['confetti', 'Confetti / McDonald’s-style', 10, 1, 'Fine, sharply defined foil fragments with bright facets.'],
  ['mirror', 'Mirror reverse', 11, 2, 'Broad smooth silver reflection.'],
  ['full-art', 'Full-art holo', 1, 3, 'Full-card rainbow sheen.'],
  ['etched', 'Etched / textured full art', 12, 3, 'Fine curved engraving under the light.'],
  ['rainbow', 'Rainbow rare', 13, 3, 'Stronger spectrum and textured sheen.'],
  ['gold', 'Gold rare', 14, 3, 'Warm metallic gold and fine speckle.'],
  ['silver', 'Silver / metal', 15, 3, 'Cool metallic reflection; no physical depth.'],
  ['radiant', 'Radiant / diamond foil', 16, 3, 'Interlocking diamond texture.'],
  ['shiny', 'Shiny / star foil', 17, 3, 'Sparkling star field and rainbow light.'],
  ['amazing', 'Amazing Rare-inspired', 18, 1, 'Colourful radial shimmer in the art window.'],
  ['oil', 'Oil-slick / prismatic', 19, 3, 'Slowly shifting interference colours.'],
  ['lenticular', 'Lenticular approximation', 20, 3, 'Angle-dependent ridges; cannot recreate alternate views.'],
].map(([id, label, pattern, region, description]) => ({ id, label, pattern, region, description }));

// Material-specific refinements relative to the shared Pattern size slider.
// Cosmos reverse and every other study deliberately keep their existing look.
export const FOIL_DETAIL_PROFILES = {
  cosmos: { scale: .5, emphasis: 1 },
  confetti: { scale: .55, emphasis: 1 },
};

const vertex = `attribute vec2 position; varying vec2 uv;
void main(){uv=position*.5+.5;gl_Position=vec4(position,0.,1.);}`;
const fragment = `precision mediump float;
varying vec2 uv;
uniform float pattern, region, strength, phase;
uniform float patternScale;
uniform float detailScale, detailEmphasis;
uniform vec2 light;
uniform vec4 artWindow;
uniform sampler2D iceMap;
float hash(vec2 p){return fract(sin(dot(p,vec2(127.1,311.7)))*43758.5453);}
vec3 rainbow(float x){return .5+.5*cos(6.28318*(x+vec3(0.,.333,.667)));}
${FOIL_V2_GLSL}
void main(){
 vec2 p=vec2(uv.x,1.-uv.y), q=p*vec2(1.,1.397)/patternScale;
 if(detailEmphasis>.5)q/=detailScale;
 float art=step(artWindow.x,p.x)*step(p.x,artWindow.x+artWindow.z)*step(artWindow.y,p.y)*step(p.y,artWindow.y+artWindow.w);
 float mask=region<.5?0.:(region<1.5?art:(region<2.5?1.-art:1.));
 if(pattern>20.5){gl_FragColor=foilV2(p,mask,art);return;}
 float sheen=pow(max(0.,1.-length((p-light)*vec2(.8,1.))),2.);
 float band=.5+.5*sin((p.x+p.y+light.x*.7-light.y*.4)*13.+phase*.25);
 vec3 colour=rainbow(p.x*.6+p.y*.35+light.x*.4+phase*.02);
 float texture=.12;
 if(pattern>1.5&&pattern<2.5){
  vec2 cell=floor(q*23.), f=fract(q*23.)-.5;
  float rnd=hash(cell), d=length(f);
  texture=step(.66,rnd)*(1.-smoothstep(.04,.13+rnd*.13,d));
  texture+=step(.91,hash(cell+9.))*(1.-smoothstep(.21,.26,d))*.55;
  if(detailEmphasis>.5){
   float radius=.09+rnd*.12;
   float disc=1.-smoothstep(radius-.015,radius+.015,d);
   float core=1.-smoothstep(.025,.065,d);
   texture=step(.66,rnd)*(disc*.75+core*.8);
   texture+=step(.91,hash(cell+9.))*(1.-smoothstep(.21,.23,d))*.75;
   colour=rainbow(rnd*.65+light.x*.3+phase*.02);
  }
 }else if(pattern<4.5&&pattern>2.5){
  float axis=(pattern<3.5?p.x:p.y)/patternScale;
  texture=pow(.5+.5*sin(axis*310.+light.x*8.),12.);
 }else if(pattern<5.5&&pattern>4.5){
  texture=pow(.5+.5*sin((q.x+q.y)*260.),12.)+pow(.5+.5*sin((q.x-q.y)*260.),12.);
 }else if(pattern<7.5&&pattern>5.5){
  vec2 f=fract(q*11.)-.5; float d=length(f);
  texture=(1.-smoothstep(.025,.055,abs(d-.31)))+(1.-smoothstep(.025,.05,abs(f.y)))*step(d,.31);
  texture+=1.-smoothstep(.04,.075,d);
  if(pattern>6.5){float m=abs(abs(f.x)-.13);texture+=step(abs(f.y+.14),.05)*step(m,.045);}
 }else if(pattern<8.5&&pattern>7.5){
  vec2 f=fract(q*10.)-.5;float a=atan(f.y,f.x);float r=length(f);
  float folded=mod(a+3.141593/6.,3.141593/3.)-3.141593/6.;
  vec2 arm=vec2(cos(folded),sin(folded))*r;
  float stem=1.-smoothstep(.012,.035,abs(arm.y));
  float branch=1.-smoothstep(.012,.03,abs(abs(arm.y)-(arm.x-.13)*.7));
  branch*=step(.13,arm.x)*step(arm.x,.29);
  texture=max(stem,branch)*(1.-smoothstep(.30,.36,r));
 }else if(pattern<9.5&&pattern>8.5){
  vec4 facet=texture2D(iceMap,fract(q));
  vec2 normal=facet.rg*2.-1.;
  float reflection=.5+.5*sin(dot(normal,light*3.-1.5)*3.+phase+facet.b*6.);
  texture=facet.a*.9+reflection*.45;
  colour=rainbow(facet.b*.55+light.x*.35+reflection*.1);
 }else if(pattern<10.5&&pattern>9.5){
  vec2 cell=floor(q*45.),f=fract(q*45.);
  texture=step(.62,hash(cell))*step(.18,f.x)*step(f.x,.7)*step(.2,f.y)*step(f.y,.55);
  colour=rainbow(hash(cell)+light.x*.3);
  if(detailEmphasis>.5)texture*=.9+step(.65,f.x+f.y)*.4;
 }else if(pattern<11.5&&pattern>10.5){colour=vec3(.85,.92,1.);texture=band*.3;
 }else if(pattern<13.5&&pattern>11.5){
  texture=pow(.5+.5*sin(length((q-vec2(.4,.8))*vec2(1.,1.3))*360.+sin(q.x*30.)*3.),10.);
  if(pattern>12.5)colour=rainbow(p.y+light.x*.3);
 }else if(pattern<15.5&&pattern>13.5){
  colour=pattern<14.5?vec3(1.,.72,.16):vec3(.78,.86,1.);
  texture=step(.93,hash(floor(q*220.)))*.7;
 }else if(pattern<16.5&&pattern>15.5){
  texture=pow(.5+.5*sin((q.x+q.y)*160.),16.)+pow(.5+.5*sin((q.x-q.y)*160.),16.);
 }else if(pattern<17.5&&pattern>16.5){
  vec2 cell=floor(q*23.),f=fract(q*23.)-.5;
  texture=step(.74,hash(cell))*(1.-smoothstep(.02,.055,min(abs(f.x),abs(f.y))))*(1.-smoothstep(.06,.28,length(f)));
 }else if(pattern<18.5&&pattern>17.5){
  colour=rainbow(atan(q.y-.5,q.x-.5)/6.28318+phase*.025);texture=pow(band,6.);
 }else if(pattern<19.5&&pattern>18.5){
  colour=rainbow(sin(q.x*8.+phase*.1)*.25+sin(q.y*12.)*.2+light.x*.5);texture=band*.25;
 }else if(pattern>19.5){
   texture=pow(.5+.5*sin(p.x/ patternScale*450.+light.x*10.),10.);colour=rainbow(light.x+p.x*.3);
 }
 float alpha=mask*strength*(.08+sheen*.25+band*.09+texture*(.23+sheen*.4));
 // Less broad wash, stronger local highlights: details stay distinct at small sizes.
 if(detailEmphasis>.5)alpha=mask*strength*((.08+sheen*.25+band*.09)*.42+texture*(.42+sheen*.65));
 if(pattern<.5)alpha=0.;
 gl_FragColor=vec4(mix(colour,vec3(1.),sheen*.35),min(alpha,.95));
}`;

export function createIceTexture() {
  const canvas = document.createElement('canvas'); canvas.width = 512; canvas.height = 512;
  const ctx = canvas.getContext('2d');
  let seed = 73491;
  const random = () => { seed = Math.imul(seed, 1664525) + 1013904223 | 0; return (seed >>> 0) / 4294967296; };
  const columns = 12, rows = 16;
  const points = Array.from({ length: rows + 1 }, (_, y) => Array.from({ length: columns + 1 }, (_, x) => [
    (x + (x > 0 && x < columns ? (random() - .5) * .65 : 0)) / columns * 512,
    (y + (y > 0 && y < rows ? (random() - .5) * .65 : 0)) / rows * 512,
  ]));
  const triangle = vertices => {
    ctx.beginPath(); vertices.forEach(([x, y], index) => index ? ctx.lineTo(x, y) : ctx.moveTo(x, y)); ctx.closePath();
    ctx.fillStyle = `rgba(${Math.floor(random() * 255)},${Math.floor(random() * 255)},${Math.floor(random() * 255)},0.12)`;
    ctx.fill(); ctx.strokeStyle = 'rgba(175,200,225,1)'; ctx.lineWidth = 1.4; ctx.stroke();
  };
  for (let y = 0; y < rows; y++) for (let x = 0; x < columns; x++) {
    const a = points[y][x], b = points[y][x + 1], c = points[y + 1][x], d = points[y + 1][x + 1];
    if (random() > .5) { triangle([a,b,d]); triangle([a,d,c]); }
    else { triangle([a,b,c]); triangle([b,d,c]); }
  }
  return canvas;
}

export function createFoilRenderer() {
  const canvas = document.createElement('canvas');
  canvas.width = 240; canvas.height = 336;
  const gl = canvas.getContext('webgl', { alpha: true, premultipliedAlpha: false, antialias: false });
  if (!gl) return null;
  const shaders = [];
  const compile = (type, text) => {
    const shader = gl.createShader(type); shaders.push(shader);
    gl.shaderSource(shader, text); gl.compileShader(shader);
    if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) throw new Error('Foil shader unavailable');
    return shader;
  };
  const program = gl.createProgram();
  let buffer, texture;
  const dispose = () => {
    if (buffer) gl.deleteBuffer(buffer);
    if (texture) gl.deleteTexture(texture);
    shaders.forEach(shader => gl.deleteShader(shader)); gl.deleteProgram(program);
    gl.getExtension('WEBGL_lose_context')?.loseContext();
  };
  try {
    gl.attachShader(program, compile(gl.VERTEX_SHADER, vertex)); gl.attachShader(program, compile(gl.FRAGMENT_SHADER, fragment));
    gl.linkProgram(program);
    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) throw new Error('Foil shader unavailable');
    gl.useProgram(program);
    buffer = gl.createBuffer(); gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1,-1,1,-1,-1,1,-1,1,1,-1,1,1]), gl.STATIC_DRAW);
    const position = gl.getAttribLocation(program, 'position');
    gl.enableVertexAttribArray(position); gl.vertexAttribPointer(position, 2, gl.FLOAT, false, 0, 0);
    texture = gl.createTexture(); gl.bindTexture(gl.TEXTURE_2D, texture);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, createIceTexture());
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR); gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE); gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    const uniforms = Object.fromEntries(['pattern', 'region', 'strength', 'phase', 'light', 'patternScale', 'detailScale', 'detailEmphasis', 'artWindow', 'iceMap'].map(key => [key, gl.getUniformLocation(program, key)]));
    gl.uniform1i(uniforms.iceMap, 0);
    return {
      draw(target, style, { strength, phase, light, patternScale = 2, artWindow = [.08, .10, .84, .375] }) {
        if (gl.isContextLost()) return false;
        gl.uniform1f(uniforms.pattern, style.pattern); gl.uniform1f(uniforms.region, style.region);
        gl.uniform1f(uniforms.strength, strength); gl.uniform1f(uniforms.phase, phase); gl.uniform2f(uniforms.light, light[0], light[1]);
        const detail = FOIL_DETAIL_PROFILES[style.id];
        gl.uniform1f(uniforms.detailScale, detail?.scale ?? 1); gl.uniform1f(uniforms.detailEmphasis, detail?.emphasis ?? 0);
        gl.uniform1f(uniforms.patternScale, patternScale); gl.uniform4f(uniforms.artWindow, ...artWindow);
        gl.viewport(0, 0, canvas.width, canvas.height); gl.drawArrays(gl.TRIANGLES, 0, 6);
        const ctx = target.getContext('2d');
        if (!ctx) return false;
        ctx.clearRect(0, 0, target.width, target.height); ctx.drawImage(canvas, 0, 0, target.width, target.height);
        return true;
      }, dispose,
    };
  } catch { dispose(); return null; }
}
